import { type ReactNode, useState } from 'react';
import { useCountdown } from '@/hooks/use-countdown';
import { useRedemptionLeadCount } from '@/hooks/use-redemption-lead-count';
import { useForfeitRedemption } from '@/hooks/use-forfeit-redemption';
import { Button } from '@/components/ui/button';
import { RedemptionStatusBanner } from './redemption-status-banner';
import { RedemptionForfeitModal } from './redemption-forfeit-modal';
import {
  HeroCountdown,
  ProgressCounter,
  HowToHint,
  EvaluatingNotice,
} from './redemption-running-parts';

const THRESHOLD_FALLBACK = 99;

type ScreenProps = {
  countdownLabel: string;
  expired: boolean;
  count: number;
  threshold: number;
  onForfeit: () => void;
};

function Screen(p: ScreenProps): ReactNode {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-lg bg-void px-base py-2xl">
      <div className="w-full max-w-[28rem] space-y-lg">
        <HeroCountdown label={p.countdownLabel} />
        <ProgressCounter count={p.count} threshold={p.threshold} />
        <RedemptionStatusBanner />
        <HowToHint />
        {p.expired && <EvaluatingNotice />}
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={p.onForfeit}
        >
          Salir del juego
        </Button>
      </div>
    </div>
  );
}

export function RedemptionRunningScreen(): ReactNode {
  const leads = useRedemptionLeadCount();
  const countdown = useCountdown(leads?.endsAt);
  const forfeit = useForfeitRedemption();
  const [forfeitOpen, setForfeitOpen] = useState(false);

  return (
    <>
      <Screen
        countdownLabel={countdown?.label ?? '...'}
        expired={!!countdown?.expired}
        count={leads?.count ?? 0}
        threshold={leads?.threshold ?? THRESHOLD_FALLBACK}
        onForfeit={() => setForfeitOpen(true)}
      />
      <RedemptionForfeitModal
        open={forfeitOpen}
        onClose={() => setForfeitOpen(false)}
        onConfirm={() => forfeit.mutate()}
        isPending={forfeit.isPending}
      />
    </>
  );
}
